import type { PartnerOrg } from '@/modules/api';

import type { OnboardingDataDraft } from '../types/onboardingDataDraft';
import type { PartnersState } from '../types/partnersState';

type ResolveSelectedOrgArgs = {
  isRestarting: boolean;
  draft: OnboardingDataDraft;
  partners: PartnersState;
  decodedOrgParam: string | null;
  /**
   * For SSO users: the bare uuid of `customerProfile.ssoPayingOrganization`
   * (extracted from its urn). Matched against the catalog's `webserverOrgUuid`
   * to skip company-search — mirrors Sparta's `getPartnerByWebserverOrgUuid`.
   */
  ssoWebserverOrgUuid?: string;
};

/**
 * Resolves the active onboarding org from rules-engine state first, falling back
 * to the SSO paying org, then the `?org=` URL hint. Pure extraction of the
 * `selectedOrg` memo in RulesEngineContext (kept as a memo there with the same
 * deps).
 *
 * - During a "start over" the draft org is already cleared; the lingering `?org=`
 *   hint is ignored too so we don't re-select the org we're navigating away from.
 * - A draft org wins: prefer the full catalog entry (matched by `key`), else
 *   reconstruct a minimal PartnerOrg from the draft fields.
 * - Otherwise, once the catalog is ready, an SSO org (matched by
 *   `webserverOrgUuid`) is consulted BEFORE the `?org=` hint — mirroring Sparta's
 *   precedence (a provisioned SSO org outranks a deep-link param).
 * - Finally, match the decoded `?org=` hint by key or name.
 */
export function resolveSelectedOrg({
  isRestarting,
  draft,
  partners,
  decodedOrgParam,
  ssoWebserverOrgUuid,
}: ResolveSelectedOrgArgs): PartnerOrg | undefined {
  if (isRestarting) return undefined;
  if (draft.orgKey) {
    const byKey =
      partners.status === 'ready' && partners.data
        ? partners.data.find((org) => org.key === draft.orgKey)
        : undefined;
    if (byKey) return byKey;
    return {
      key: draft.orgKey,
      uuid: draft.orgUuid,
      name: draft.orgName,
      supportsEligibilityCheck: Boolean(draft.orgEligibilityCheckFields),
      eligibilityCheckFields: draft.orgEligibilityCheckFields,
    } as PartnerOrg;
  }
  if (partners.status !== 'ready' || !partners.data) return undefined;
  if (ssoWebserverOrgUuid) {
    // Guard the truthiness of `webserverOrgUuid` so a catalog entry without one
    // can't match an undefined target — and don't match `undefined === undefined`.
    const ssoOrg = partners.data.find(
      (org) => org.webserverOrgUuid && org.webserverOrgUuid === ssoWebserverOrgUuid
    );
    if (ssoOrg) return ssoOrg;
  }
  if (decodedOrgParam) {
    return partners.data.find(
      (org) => org.key === decodedOrgParam || org.name.toLowerCase() === decodedOrgParam
    );
  }
  return undefined;
}
